import React, { useCallback, useEffect, useState } from 'react'
import { Activity, Category, CategoryListResponse } from '../../../services/network'
import { Avatar, Button, Flex, HStack, Input, InputGroup, InputLeftElement, Spacer, Text } from '@chakra-ui/react'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import * as eva from 'eva-icons'
import { ModifyCategoryModal } from '../../../components/ModifyCategoryModal/ModifyCategoryModal'
import { ModifyActivityModal } from '../../../components/ModifyActivityModal/ModifyActivityModal'
import { useTranslation } from 'react-i18next'

interface ISystemPageProps {
  categories: Category[]
  setCategories: React.Dispatch<React.SetStateAction<CategoryListResponse | undefined>>
}

function SystemPage({ categories, setCategories }: ISystemPageProps): JSX.Element {
  const [category, setCategory] = useState<Category>()
  const [activityState, setActivityState] = useState<Activity>()
  const { t } = useTranslation()
  const [isModifyCategoryModalOpen, setIsModifyCategoryModalOpen] = useState(false)
  const [isModifyActivityModalOpen, setIsModifyActivityModalOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [filteredCategories, setFilteredCategories] = useState<Category[]>([])

  const [collapsedCategories, setCollapsedCategories] = useState<Record<number, boolean>>({})
  const toggleCollapse = (index: number): void => {
    setCollapsedCategories((prevState) => ({
      ...prevState,
      [index]: !prevState[index]
    }))
  }

  const handleModifyButton = (category: Category): void => {
    setCategory(category)
    setIsModifyCategoryModalOpen(true)
  }

  const handleActivityButton = (activity: Activity): void => {
    setActivityState(activity)
    setIsModifyActivityModalOpen(true)
  }

  const search = useCallback(
    (text: string): void => {
      let categoriesFiltered = categories
      if (text !== '') {
        categoriesFiltered = categories
          .filter((category) => (category.activities || []).some((activity: Activity) => activity.name?.it.toLowerCase().includes(text.toLowerCase())))
          .map((filteredCategory) => ({
            ...filteredCategory,
            activities: (filteredCategory.activities || [])
              .filter((activity: Activity) => activity.name?.it.toLowerCase().includes(text.toLowerCase()))
              .sort((a: Activity, b: Activity) => {
                if (a.name && b.name) {
                  return a.name.it.localeCompare(b.name.it)
                }
                return 0
              })
          }))
      }
      setFilteredCategories(categoriesFiltered.sort((c1, c2) => c1.description.it.localeCompare(c2.description.it)))
    },
    [categories]
  )

  useEffect(() => {
    search(query)
  }, [query, filteredCategories, search])

  useEffect(() => eva.replace())

  return (
    <Flex direction={'column'}>
      <HStack spacing={4} height={'52px'}>
        <InputGroup>
          <InputLeftElement pointerEvents='none'>
            <i data-eva={'search-outline'}></i>
          </InputLeftElement>
          <Input
            placeholder={t('search_activity_placeholder')}
            variant={'search'}
            bg={'NeutralLight'}
            borderWidth='1px'
            borderColor='greyCold200'
            borderStyle='solid'
            borderRadius={'16px'}
            gap={'16px'}
            value={query}
            onChange={(e): void => setQuery(e.target.value)}
          />
        </InputGroup>
      </HStack>
      {filteredCategories.length > 0 ? (
        filteredCategories.map((category, index) => (
          <Flex
            key={index}
            background={'rgba(143, 155, 179, 0.16)'}
            borderRadius={'12px'}
            padding={'16px'}
            gap={'14px'}
            direction={'column'}
            marginTop={'10px'}
            flexShrink={'0px'}>
            <HStack key={'hstack'}>
              <Avatar size='md' name='avatar' src={category.iconUrl}></Avatar>
              <Text fontSize='16px' fontWeight='600' lineHeight='22.4px' color={'#131313'}>
                {category.description.it}
              </Text>
              <Spacer></Spacer>
              {!collapsedCategories[index] && (
                <Button
                  key={'modify'}
                  onClick={(): void => {
                    handleModifyButton(category)
                  }}>
                  <i data-eva={'edit-2-outline'} data-eva-fill={'#DD8B25'}></i>
                </Button>
              )}
              {!collapsedCategories[index] ? (
                <Button
                  key={'minus'}
                  onClick={(): void => {
                    toggleCollapse(index)
                  }}>
                  <i data-eva='minus-circle-outline' data-eva-fill={'#DD8B25'}></i>
                </Button>
              ) : (
                <Button
                  key={'plus'}
                  onClick={(): void => {
                    toggleCollapse(index)
                  }}>
                  <i data-eva='plus-circle-outline' data-eva-fill={'#DD8B25'}></i>
                </Button>
              )}
            </HStack>
            {!collapsedCategories[index] && (
              <HStack>
                <Flex flexWrap={'wrap'} gap={'6px'}>
                  {category.activities ? (
                    category.activities.map((activity) => (
                      <Flex key={activity.id} minWidth='100px' minHeight='50px' width='fit-content' height='fit-content'>
                        <Button
                          key={'button'}
                          background={'NeutralLight'}
                          borderRadius={'600px'}
                          padding={'8px, 10px, 8px, 10px'}
                          gap={'8px'}
                          onClick={(): void => handleActivityButton(activity)}>
                          <Text key={'text'} fontSize='14px' fontWeight='600' lineHeight='19.4px' color={'#131313'}>
                            {activity.name.it}
                          </Text>
                          <i data-eva={'edit-2'} data-eva-fill={'black'} data-eva-height={'16px'} data-eva-width={'16px'}></i>
                        </Button>
                      </Flex>
                    ))
                  ) : (
                    <></>
                  )}
                </Flex>
              </HStack>
            )}
          </Flex>
        ))
      ) : (
        <Text marginLeft={'2px'} fontWeight='600' fontSize='16px' lineHeight='22.4px' color={'greyCold300'}>
          {t('no_activity_found')}
        </Text>
      )}
      {category ? (
        <ModifyCategoryModal
          isOpen={isModifyCategoryModalOpen}
          onClose={(): void => {
            setIsModifyCategoryModalOpen(false)
          }}
          category={category}
          setIsModifyCategoryModalOpen={setIsModifyCategoryModalOpen}
          categories={categories}
          setCategories={setCategories}
        />
      ) : (
        <></>
      )}
      {activityState ? (
        <ModifyActivityModal
          isOpen={isModifyActivityModalOpen}
          onClose={(): void => {
            setIsModifyActivityModalOpen(false)
          }}
          categories={categories}
          activity={activityState}
          setIsModifyActivityModalOpen={setIsModifyActivityModalOpen}
          setCategories={setCategories}
        />
      ) : (
        <></>
      )}
    </Flex>
  )
}

export { SystemPage }
